import { ref, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'

/**
 * WebSocket 连接管理 composable
 *
 * 特性:
 * - 自动重连（指数退避: 1s/2s/4s/8s/16s/最大30s）
 * - 连接状态追踪
 * - 消息回调注册
 * - 自动清理
 */
export interface WebSocketOptions {
  /** WebSocket 端点路径 */
  endpoint: string
  /** 是否自动连接 */
  autoConnect?: boolean
  /** 最大重连次数，-1 表示无限 */
  maxReconnectAttempts?: number
  /** 重连间隔基数(ms) */
  reconnectBaseMs?: number
  /** 最大重连间隔(ms) */
  maxReconnectMs?: number
  /** 心跳间隔(ms) */
  heartbeatIntervalMs?: number
}

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting'

export function useWebSocket(options: WebSocketOptions) {
  const {
    endpoint,
    autoConnect = true,
    maxReconnectAttempts = 10,
    reconnectBaseMs = 1000,
    maxReconnectMs = 30000,
    heartbeatIntervalMs = 30000,
  } = options

  const status = ref<ConnectionStatus>('disconnected')
  const lastMessage = ref<any>(null)
  const reconnectAttempts = ref(0)

  let ws: WebSocket | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null
  const messageHandlers: Array<(data: any) => void> = []

  function getWsUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    return `${protocol}//${host}${endpoint}`
  }

  function startHeartbeat() {
    stopHeartbeat()
    heartbeatTimer = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }))
      }
    }, heartbeatIntervalMs)
  }

  function stopHeartbeat() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
  }

  function scheduleReconnect() {
    if (maxReconnectAttempts >= 0 && reconnectAttempts.value >= maxReconnectAttempts) {
      status.value = 'disconnected'
      ElMessage.warning('WebSocket 重连次数已达上限, 请刷新页面')
      return
    }
    const delay = Math.min(reconnectBaseMs * Math.pow(2, reconnectAttempts.value), maxReconnectMs)
    status.value = 'reconnecting'
    reconnectTimer = setTimeout(() => {
      reconnectAttempts.value++
      connect()
    }, delay)
  }

  function connect() {
    if (ws?.readyState === WebSocket.OPEN || ws?.readyState === WebSocket.CONNECTING) {
      return
    }
    try {
      status.value = 'connecting'
      ws = new WebSocket(getWsUrl())

      ws.onopen = () => {
        status.value = 'connected'
        reconnectAttempts.value = 0
        startHeartbeat()
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'pong') return
          lastMessage.value = data
          messageHandlers.forEach((handler) => {
            try {
              handler(data)
            } catch (e) {
              console.error('[WebSocket] handler error:', e)
            }
          })
        } catch {
          // ignore non-JSON messages
        }
      }

      ws.onclose = (_event) => {
        stopHeartbeat()
        if (status.value !== 'disconnected') {
          scheduleReconnect()
        }
      }

      ws.onerror = () => {
        // onclose will be called after onerror
      }
    } catch (e) {
      scheduleReconnect()
    }
  }

  function disconnect() {
    status.value = 'disconnected'
    stopHeartbeat()
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    if (ws) {
      ws.onclose = null // prevent auto-reconnect
      ws.close()
      ws = null
    }
  }

  function send(data: any) {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(typeof data === 'string' ? data : JSON.stringify(data))
    }
  }

  function onMessage(handler: (data: any) => void) {
    messageHandlers.push(handler)
    // 返回取消注册函数
    return () => {
      const idx = messageHandlers.indexOf(handler)
      if (idx >= 0) messageHandlers.splice(idx, 1)
    }
  }

  if (autoConnect) {
    connect()
  }

  onUnmounted(() => {
    disconnect()
  })

  return {
    status,
    lastMessage,
    connect,
    disconnect,
    send,
    onMessage,
  }
}
